import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import {
  getAutomationTasks,
  retryAutomationTask,
} from '@/features/automation/api';
import {
  actionLabel,
  AutomationTaskStateSchema,
  CONTRACT_LEVEL_INSTALLMENT,
  DEVICE_ACTIONS,
  type AutomationTask,
  type AutomationTaskState,
} from '@/features/automation/types';

import { ListPageShell } from '@/components/layout/ListPageShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copyable-id';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RotateCw,
  Smartphone,
  Timer,
} from '@/lib/icons';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/store/hooks';
import { hasPermission } from '@/lib/auth/permissions';
import { cn, truncateId } from '@/lib/utils';
import {
  RelativeTime,
  StackedTimestamp,
} from '@/components/common/relative-time';
import { TimeZoneHint } from '@/components/common/time-zone-hint';

const PAGE_SIZE = 25;

/**
 * State lives in the URL so an operator can send a colleague the exact list
 * they are looking at, and so a reload does not silently switch which class of
 * problem is on screen. Same convention as /feedback and /support.
 */
const searchSchema = z.object({
  state: AutomationTaskStateSchema.optional(),
  page: z.number().min(1).optional(),
});

export const Route = createFileRoute('/_layout/lock-engine/automation')({
  validateSearch: searchSchema,
  component: AutomationHealthPage,
});

/**
 * The two classes, and why they are two.
 *
 * Merging them would be the obvious simplification and the wrong one: an
 * exhausted row is a provider that said no six times and will never be asked
 * again without a human; a stalled row is a *worker* that died holding work the
 * engine will hand to the next one. One needs a button, the other needs
 * somebody to notice the count is growing. The same table with the same
 * "retry" affordance on both would teach an operator to press it on rows where
 * pressing does nothing.
 */
const TABS: ReadonlyArray<{
  state: AutomationTaskState;
  label: string;
  blurb: string;
}> = [
  {
    state: 'exhausted',
    label: 'Needs attention',
    blurb:
      'Actions that used every attempt and stopped. Nothing will retry these until you do — a device that failed to lock is still unlocked, and a customer whose unlock failed is still locked out.',
  },
  {
    state: 'stalled',
    label: 'Stuck in flight',
    blurb:
      'Actions claimed by a worker that never finished them. The engine takes each one over on a later pass, so there is nothing to press here — but a list that keeps growing means workers are dying mid-action.',
  },
] as const;

function StateTab({
  active,
  label,
  onClick,
}: Readonly<{ active: boolean; label: string; onClick: () => void }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'border-brand-500 bg-brand-50 text-foreground'
          : 'border-border-light bg-card text-muted-foreground hover:bg-surface-container-low/60'
      )}
    >
      {label}
    </button>
  );
}

function AttemptBadge({ task }: Readonly<{ task: AutomationTask }>) {
  const spent = task.attemptCount >= task.maxAttempts;
  return (
    <Badge
      variant="outline"
      className={cn(
        'tabular-nums',
        spent && 'border-red-500/30 bg-red-500/15 text-red-600 dark:text-red-400'
      )}
    >
      {task.attemptCount} / {task.maxAttempts}
    </Badge>
  );
}

function ActionCell({ task }: Readonly<{ task: AutomationTask }>) {
  const touchesDevice = DEVICE_ACTIONS.includes(task.action);
  return (
    <div className="flex items-center gap-2">
      {touchesDevice ? (
        <Smartphone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      ) : null}
      <div>
        <div className="text-sm font-medium">{actionLabel(task.action)}</div>
        <div className="text-xs text-muted-foreground">
          {task.installmentNumber === CONTRACT_LEVEL_INSTALLMENT
            ? 'Whole contract'
            : `Installment ${task.installmentNumber}`}
        </div>
      </div>
    </div>
  );
}

function TimeCell({ value }: Readonly<{ value: string | null }>) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <StackedTimestamp value={value} className="whitespace-nowrap" />;
}

function EmptyState({ state }: Readonly<{ state: AutomationTaskState }>) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
      <CheckCircle2 className="h-8 w-8 text-emerald-600" />
      <p className="text-sm font-medium">
        {state === 'exhausted'
          ? 'No automation is waiting on a human'
          : 'No worker is stuck mid-action'}
      </p>
      <p className="max-w-sm text-sm text-muted-foreground">
        {state === 'exhausted'
          ? 'Every reminder, lock and unlock either succeeded or still has attempts left.'
          : 'Every claimed action is inside its lease.'}
      </p>
    </div>
  );
}

function AutomationHealthPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const user = useAppSelector((s) => s.auth.user);
  // Requeuing writes, so it needs `update` — the same rule the API enforces.
  const canRetry = hasPermission(user, 'lock_engine', 'update');

  const state = search.state ?? 'exhausted';
  const page = search.page ?? 1;
  const filters = {
    state,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  };

  const { data: result, isLoading } = useQuery({
    queryKey: ['automation-tasks', filters],
    queryFn: () => getAutomationTasks(filters),
    // A stuck row is not urgent to the second, but an operator working the
    // list wants it to reflect the worker's last pass without a manual reload.
    refetchInterval: 60_000,
    staleTime: 15_000,
  });

  const retry = useMutation({
    mutationFn: (taskId: string) => retryAutomationTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation-tasks'] });
      // Also refresh the lock queue: a requeued device action reappears there
      // as soon as the next pass picks it up.
      queryClient.invalidateQueries({ queryKey: ['lock-engine'] });
      toast({
        title: 'Queued for retry',
        description: 'The action is due now and the next pass will pick it up.',
      });
    },
    onError: (error: Error) =>
      toast({
        variant: 'destructive',
        title: 'Could not retry',
        description:
          error.message ||
          'The task is no longer an exhausted task of this tenant.',
      }),
  });

  const rows = result?.succeeded ? (result.value ?? []) : [];
  const tab = TABS.find((t) => t.state === state) ?? TABS[0];

  const goTo = (next: Partial<{ state: AutomationTaskState; page: number }>) =>
    navigate({
      to: '/lock-engine/automation',
      search: (prev) => ({ ...prev, ...next }),
    });

  return (
    <ListPageShell
      title="Automation Health"
      description="Reminders, locks, unlocks and completions that failed or never finished."
      actions={
        <Link to="/lock-engine">
          <Button variant="outline" size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Lock Engine
          </Button>
        </Link>
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <StateTab
              key={t.state}
              active={t.state === state}
              label={t.label}
              onClick={() => goTo({ state: t.state, page: 1 })}
            />
          ))}
        </div>
      }
      hasData
    >
      <div className="flex items-start gap-2 border-b border-border-light px-4 py-3 text-sm text-muted-foreground md:px-5">
        {state === 'exhausted' ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        ) : (
          <Timer className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <p className="max-w-3xl">{tab.blurb}</p>
      </div>

      {isLoading ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState state={state} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Contract</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>
                  {state === 'exhausted' ? 'Last error' : 'Claimed'}
                </TableHead>
                <TableHead>
                  <span className="inline-flex items-center gap-1.5">
                    Scheduled
                    <TimeZoneHint />
                  </span>
                </TableHead>
                {state === 'exhausted' ? (
                  <TableHead className="text-right">Retry</TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((task) => (
                <TableRow key={task.id}>
                  <TableCell>
                    <ActionCell task={task} />
                  </TableCell>

                  <TableCell className="whitespace-nowrap">
                    {/* Link and copy button are siblings, not nested — an <a>
                        may not contain a <button>. */}
                    <span className="group/id inline-flex items-center gap-1.5">
                      <Link
                        to="/contracts/$contractId"
                        params={{ contractId: task.contractId }}
                        title={task.contractId}
                        className="rounded-sm font-mono text-sm tracking-tight text-foreground transition-colors hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                      >
                        {truncateId(task.contractId)}
                      </Link>
                      <CopyButton value={task.contractId} label="Copy contract id" />
                    </span>
                  </TableCell>

                  <TableCell>
                    <AttemptBadge task={task} />
                  </TableCell>

                  <TableCell className="max-w-md">
                    {state === 'exhausted' ? (
                      <span
                        className="line-clamp-2 text-sm text-muted-foreground"
                        // The row shows two lines; the provider's full
                        // complaint is what identifies the outage.
                        title={task.lastError ?? undefined}
                      >
                        {task.lastError ?? 'No error recorded'}
                      </span>
                    ) : (
                      <TimeCell value={task.claimedAt} />
                    )}
                  </TableCell>

                  <TableCell>
                    <TimeCell value={task.scheduledFor} />
                    {/* On an exhausted row the scheduled time is a retry that
                        never came, so when it gave up is the more useful of the
                        two — shown alongside rather than instead of it. */}
                    {state === 'exhausted' ? (
                      <div className="mt-1 text-xs text-muted-foreground">
                        gave up <RelativeTime value={task.lastModifiedAt} />
                      </div>
                    ) : null}
                  </TableCell>

                  {state === 'exhausted' ? (
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!canRetry || retry.isPending}
                        title={
                          canRetry
                            ? undefined
                            : 'Retrying needs the lock engine update permission'
                        }
                        onClick={() => retry.mutate(task.id)}
                      >
                        {retry.isPending && retry.variables === task.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCw className="h-4 w-4" />
                        )}
                        <span className="ml-2">Retry</span>
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* The endpoint returns a page and no total — counting a table that holds
          ~85 rows per contract and never prunes is the one query that cannot
          stay bounded. A full page means there may be more, so Next is offered
          only then. Same convention as /feedback. */}
      {rows.length > 0 ? (
        <div className="flex items-center justify-between border-t border-border-light px-4 py-3 md:px-5">
          <span className="text-sm text-muted-foreground">
            Showing {rows.length}
            {page > 1 ? ` · page ${page}` : ''}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => goTo({ page: page - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={rows.length < PAGE_SIZE}
              onClick={() => goTo({ page: page + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </ListPageShell>
  );
}
